import { useEffect, useRef, useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { useBusinessSettingsStore } from "../admin/store/businessSettingsStore";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function Contact() {
  const ref = useInView();
  const { settings } = useBusinessSettingsStore();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Contact Us</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Let's <span className="gradient-text">Talk</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              Get in touch for a free consultation. We're here to help you find the perfect security and technology solution.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="animate-on-scroll font-display text-2xl font-bold mb-8">Get In Touch</h2>
              <div className="space-y-6">
                <a href="tel:+94762829800" className="animate-on-scroll stagger-1 flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center group-hover:bg-brand-orange transition-colors">
                    <Phone size={20} className="text-brand-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Call Us</p>
                    <p className="text-brand-muted text-sm">076 282 9800</p>
                  </div>
                </a>
                <a href="https://wa.me/94702161323" target="_blank" rel="noopener noreferrer" className="animate-on-scroll stagger-2 flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center group-hover:bg-brand-orange transition-colors">
                    <MessageCircle size={20} className="text-brand-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">WhatsApp</p>
                    <p className="text-brand-muted text-sm">070 216 1323</p>
                  </div>
                </a>
                <a href="mailto:info@k-tec.lk" className="animate-on-scroll stagger-3 flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center group-hover:bg-brand-orange transition-colors">
                    <Mail size={20} className="text-brand-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Email</p>
                    <p className="text-brand-muted text-sm">info@k-tec.lk</p>
                  </div>
                </a>
                <div className="animate-on-scroll stagger-4 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                    <MapPin size={20} className="text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Location</p>
                    <p className="text-brand-muted text-sm">Kandy, Sri Lanka</p>
                  </div>
                </div>
                <div className="animate-on-scroll stagger-5 flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center">
                    <Clock size={20} className="text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Working Hours</p>
                    <p className="text-brand-muted text-sm">Mon - Sat: 9:00 AM - 6:00 PM</p>
                    <p className="text-brand-muted text-sm">24/7 Emergency Support</p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="animate-on-scroll stagger-6 mt-8 bg-brand-gray rounded-2xl h-48 flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={32} className="text-brand-orange/30 mx-auto mb-2" />
                  <p className="text-sm text-brand-muted">Kandy, Sri Lanka</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-card border border-brand-border/50 overflow-hidden">
                <h2 className="animate-on-scroll font-display text-2xl font-bold mb-2">Send a Message</h2>
                <p className="animate-on-scroll stagger-1 text-sm text-brand-muted mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="font-semibold text-xl mb-2">Message Sent!</h3>
                    <p className="text-brand-muted">Thank you. We'll contact you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Full Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="w-full px-4 py-3 bg-brand-gray rounded-xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="w-full px-4 py-3 bg-brand-gray rounded-xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="07X XXX XXXX"
                          className="w-full px-4 py-3 bg-brand-gray rounded-xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Subject</label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-brand-gray rounded-xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-sm"
                        >
                          <option value="">Select service</option>
                          <option value="cctv">CCTV Systems</option>
                          <option value="alarm">Alarm Systems</option>
                          <option value="smarthome">Smart Home</option>
                          <option value="starlink">Starlink Internet</option>
                          <option value="networking">Networking</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Message</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        placeholder="Tell us about your project or requirements..."
                        className="w-full px-4 py-3 bg-brand-gray rounded-xl border-2 border-transparent focus:border-brand-orange focus:bg-white outline-none transition-all text-sm resize-none"
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full">
                      Send Message <Send size={16} className="ml-2" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      {settings.mapsLink && (
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-8">
              <h2 className="text-[24px] font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
                <MapPin className="w-5 h-5 text-brand-orange" /> Find Us on the Map
              </h2>
              <p className="text-sm text-gray-500">{settings.address}</p>
            </div>
            <div className="rounded-3xl overflow-hidden border border-gray-200/60 shadow-sm h-80">
              <iframe
                src={settings.mapsLink.includes('embed') ? settings.mapsLink : `https://maps.google.com/maps?q=${encodeURIComponent(settings.address)}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="K-Tec Location"
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
